import { Spacing } from '@persona-id/design-tokens';
import produce from 'immer';
import { capitalize, intersection, snakeCase } from 'lodash/fp';
import isEqual from 'react-fast-compare';
import { Controller, get, useFormContext } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import {
  DEFAULT_MAX_CHAR_LENGTH,
  DEFAULT_REDACTION_POLICY,
  DEFAULT_SANITIZE,
  DEFAULT_WRITE_POLICY,
} from 'dashboard/apps/InquiryTemplateEditor/FieldPanel/shared';
import ReactSelect, { ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import {
  DEFAULT_ARRAY_ITEM_SCHEMA,
  DEFAULT_FIELD_SCHEMA,
  EditorableSchema,
  FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY,
  FieldSchemaEditorSupportedTypes,
  RelationSchemaType,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import { getAllFieldKeys } from 'dashboard/components-lib/FieldSchemaEditor/utils';
import { ConfigSection, InputLabel } from 'dashboard/components-lib/FieldSchemaEditorForm/shared';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Paragraph } from 'design-system/components/Text';
import { Hairline } from 'design-system/variables/effect';
import { FieldSchema, FieldSchemaConfig, FieldSchemaType } from 'lib/fields';

const DEFAULT_FIELD_SCHEMA_PANEL_WIDTH = 400;
const MAX_RECOMMENDED_NESTING_DEPTH = 8;

const SUPPORTED_FIELD_TYPE_OPTIONS = [
  FieldSchemaType.Array,
  FieldSchemaType.Boolean,
  FieldSchemaType.Date,
  FieldSchemaType.File,
  FieldSchemaType.Hash,
  FieldSchemaType.Integer,
  FieldSchemaType.MultiChoices,
  FieldSchemaType.Choices,
  FieldSchemaType.Number,
  FieldSchemaType.String,
  RelationSchemaType.RelationSchema,
].sort((option1, option2) => {
  const option1Name = FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option1];
  const option2Name = FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option2];
  if (option1Name && option2Name) {
    return option1Name.localeCompare(option2Name);
  } else {
    return option1.localeCompare(option2);
  }
});

const SUPPORTED_ARRAY_TYPES_OPTIONS = [
  FieldSchemaType.Boolean,
  FieldSchemaType.Hash,
  FieldSchemaType.File,
  FieldSchemaType.Integer,
  FieldSchemaType.Number,
  FieldSchemaType.String,
].sort((option1, option2) => {
  const option1Name = FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option1];
  const option2Name = FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option2];
  if (option1Name && option2Name) {
    return option1Name.localeCompare(option2Name);
  } else {
    return option1.localeCompare(option2);
  }
});

const DEFAULT_VALUE_SUPPORTED_FIELD_TYPES = [
  FieldSchemaType.Boolean,
  FieldSchemaType.Choices,
  FieldSchemaType.MultiChoices,
  FieldSchemaType.Number,
  FieldSchemaType.String,
];

const DefaultValueInput = ({
  fieldSchema,
  onChange,
}: {
  fieldSchema: FieldSchema;
  onChange: (value: string | number | boolean | null) => void;
}) => {
  const { 'default-value': value } = fieldSchema;
  const sharedProps = {
    id: 'field-schema-panel__field-default-value',
    placeholder: 'Set a default value (optional)',
  };
  switch (fieldSchema.type) {
    case FieldSchemaType.Boolean: {
      const currentValue = value == null ? '' : value ? 'true' : 'false';
      return (
        <Select
          value={currentValue}
          onValueChange={(val: string) => {
            const newValue = val === '' ? null : val === 'true' ? true : false;
            onChange(newValue);
          }}
          {...sharedProps}
        >
          {['true', 'false'].map((val) => (
            <Option key={val} value={val}>
              {capitalize(val)}
            </Option>
          ))}
        </Select>
      );
    }
    case FieldSchemaType.Choices:
      return (
        <Select
          value={value}
          onValueChange={(val: string) => {
            onChange(val);
          }}
          {...sharedProps}
        >
          {(fieldSchema.config.options ?? []).map((option) => (
            <Option key={option} value={option}>
              {option}
            </Option>
          ))}
        </Select>
      );
    case FieldSchemaType.MultiChoices:
      return (
        <ReactSelect
          value={value}
          onChange={onChange}
          options={(fieldSchema.config.options ?? []).map((option) => ({
            label: option,
            value: option,
          }))}
          isMulti
          {...sharedProps}
        />
      );
    case FieldSchemaType.Number:
      return (
        <Input
          role='input'
          type='number'
          value={value}
          onChange={(e) => {
            const defaultValue = e.target.value === '' ? null : parseInt(e.target.value);
            onChange(defaultValue);
          }}
          {...sharedProps}
        />
      );
    case FieldSchemaType.String:
      return (
        <Input
          role='input'
          type='string'
          value={value}
          onChange={(e) => {
            const defaultValue = e.target.value === '' ? null : e.target.value;
            onChange(defaultValue);
          }}
          {...sharedProps}
        />
      );
    default:
      return null;
  }
};

const getKeyPath = (
  fieldName: string,
  allFields: { key: string; path: string }[],
): string | undefined => {
  const fieldNameParts = fieldName.split('.');
  return allFields.find(
    ({ path }) => path === fieldNameParts.slice(1, fieldNameParts.length).join('.'),
  )?.key;
};

const getFieldSchemaWithUpdatedType = (
  fieldSchema: EditorableSchema,
  type: EditorableSchema['type'],
): FieldSchemaEditorSupportedTypes => {
  return produce(fieldSchema, (draftSchema: EditorableSchema) => {
    draftSchema.type = type;

    if (draftSchema.type !== RelationSchemaType.RelationSchema) {
      draftSchema['default-value'] = null;
    }

    draftSchema.config = Object.fromEntries(
      Object.keys(draftSchema.config)
        .filter((key) => key in ({} as FieldSchemaConfig))
        .map((key) => [key, draftSchema.config[key]]),
    );

    switch (draftSchema.type) {
      case FieldSchemaType.Array: {
        draftSchema.config['item-schema'] = DEFAULT_ARRAY_ITEM_SCHEMA;
        break;
      }
      case FieldSchemaType.Choices:
      case FieldSchemaType.MultiChoices: {
        draftSchema.config.options = [];
        break;
      }
      case FieldSchemaType.Hash: {
        draftSchema.config['item-schemas'] = [DEFAULT_FIELD_SCHEMA];
        break;
      }
      case FieldSchemaType.String: {
        draftSchema.config['max-char-length'] = DEFAULT_MAX_CHAR_LENGTH;
        draftSchema.config['redaction-policy'] = DEFAULT_REDACTION_POLICY;
        draftSchema.config['write-policy'] = DEFAULT_WRITE_POLICY;
        draftSchema.config.sanitize = DEFAULT_SANITIZE;
        break;
      }
      case RelationSchemaType.RelationSchema: {
        draftSchema.cardinality = 'many';
        break;
      }
      default:
        break;
    }
  });
};

const FieldSchemaPanelContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.primary};
  width: ${DEFAULT_FIELD_SCHEMA_PANEL_WIDTH}px;
  height: 100%;
  flex: none;
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
  display: flex;
  flex-direction: column;
  position: relative;
`;

const HeadingContainer = styled.div`
  padding: ${Spacing[5]};
`;

const PaddedConfigSection = styled(ConfigSection)`
  padding: ${Spacing[5]};
  overflow-y: auto;
`;

const FieldConfigurationControl = styled.div`
  margin-top: auto;
  box-shadow: ${Hairline.Top};
  padding: ${Spacing[4]} ${Spacing[5]};
  width: 100%;
  background-color: ${({ theme }) => theme.color.background.primary};
`;

const FieldButtonGroup = styled(HStack)`
  width: fit-content;
`;

interface FormFieldSchemaPanelProps {
  fieldName: string;
  allFields: { key: string; path: string; fieldSchema: FieldSchemaEditorSupportedTypes }[];
  systemFieldSchemas?: FieldSchema[];
  allowRelation?: boolean;
  editingEnabled: boolean;
  canEdit: (fieldSchema: EditorableSchema) => boolean;
  renderAdditionalPanelContent?: (
    fieldSchema: EditorableSchema,
    fieldName: string,
    formContext: ReturnType<typeof useFormContext>,
  ) => React.ReactNode;
  onClose: () => void;
  onDelete: () => void;
}

const FormFieldSchemaPanel: React.FC<FormFieldSchemaPanelProps> = ({
  fieldName,
  allFields,
  systemFieldSchemas = [],
  allowRelation,
  renderAdditionalPanelContent,
  editingEnabled,
  canEdit,
  onClose,
}) => {
  const formContext = useFormContext();
  const {
    control,
    formState: { errors },
    getValues,
    register,
    setValue,
  } = formContext;
  const fieldSchema: EditorableSchema = getValues(fieldName);
  if (fieldSchema == null) return null;

  const currentKeyPath = getKeyPath(fieldName, allFields);
  const systemFieldKeys = getAllFieldKeys(systemFieldSchemas);
  const isSystemField = systemFieldKeys.some((systemFieldKey) => systemFieldKey === currentKeyPath);
  const isTopLevelSystemField = systemFieldKeys
    .filter((k) => !k.includes('.'))
    .some((systemFieldKey) => systemFieldKey === currentKeyPath);
  const allowHash = fieldName.split('.').length < MAX_RECOMMENDED_NESTING_DEPTH;

  const fieldSchemaCollisions = allFields.filter(({ key }) => key === currentKeyPath);
  const hasActiveFieldSchemaCollisions =
    fieldSchemaCollisions.filter(({ fieldSchema }) => fieldSchema.config['deactivated-at'] == null)
      .length > 1;
  const hasInactiveFieldSchemaCollisions = fieldSchemaCollisions.some(
    ({ fieldSchema }) => fieldSchema.config['deactivated-at'] != null,
  );
  let keyCollisionErrorMessage;
  if (hasInactiveFieldSchemaCollisions) {
    keyCollisionErrorMessage = 'Field key already exists for a deactivated field';
  } else if (hasActiveFieldSchemaCollisions) {
    keyCollisionErrorMessage = 'Field key must be unique';
  }

  const fieldTypeOptions = SUPPORTED_FIELD_TYPE_OPTIONS.filter(
    (option) =>
      allowHash || option !== FieldSchemaType.Hash || fieldSchema.type === FieldSchemaType.Hash,
  )
    .filter(
      (option) =>
        allowRelation ||
        option !== RelationSchemaType.RelationSchema ||
        fieldSchema.type === RelationSchemaType.RelationSchema,
    )
    .map((option) => (
      <Option key={option} value={option}>
        {FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option]}
      </Option>
    ));

  const arrayTypeOptions = SUPPORTED_ARRAY_TYPES_OPTIONS.filter(
    (option) =>
      allowHash ||
      option !== FieldSchemaType.Hash ||
      (fieldSchema.type === FieldSchemaType.Array &&
        fieldSchema.config['item-schema'].type === FieldSchemaType.Hash),
  ).map((option) => (
    <Option key={option} value={option}>
      {FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[option]}
    </Option>
  ));

  // Editing is always disabled for key and type for persisted fields
  const canEditKeyType = !fieldSchema.persisted && editingEnabled;

  // Editing for other fields is enabled if the field is not persisted or we allow
  // editing of persisted fields
  const canEditField = canEdit(fieldSchema) && editingEnabled;

  const matchingSystemField = systemFieldSchemas.find(
    (systemField) => systemField.key === currentKeyPath,
  );
  const persistSystemField = () => {
    if (matchingSystemField) {
      setValue(fieldName, matchingSystemField);
    }
  };

  return (
    <FieldSchemaPanelContainer role='form' aria-label='Field schema editor'>
      <HeadingContainer>
        <Heading size='sm'>Field details</Heading>
      </HeadingContainer>
      <PaddedConfigSection>
        <InputLabel htmlFor='field-schema-panel__field-label'>Label</InputLabel>
        <Input
          id='field-schema-panel__field-label'
          {...register(`${fieldName}.label`, {
            required: 'Field must have a label',
            onChange: (e) => {
              if (!canEditKeyType) {
                // Don't auto-generate key if we're just editing the label in an
                // already persisted schema
                return;
              }

              const newFieldKey = snakeCase(e.target.value);
              setValue(`${fieldName}.key`, newFieldKey);
            },
          })}
          placeholder='label'
          disabled={isSystemField && !(fieldName.split('.').length === 2)}
          autoFocus
        />
        {get(errors, `${fieldName}.label.message`) && (
          <FormError>{get(errors, `${fieldName}.label.message`)}</FormError>
        )}
        <Spacer height={2} />
        <InputLabel htmlFor='field-schema-panel__field-key'>Key</InputLabel>
        <fieldset disabled>
          <Input
            id='field-schema-panel__field-key'
            {...register(`${fieldName}.key`, {
              required: 'Field must have a key',
            })}
            placeholder='--'
          />
        </fieldset>
        {keyCollisionErrorMessage && <FormError>{keyCollisionErrorMessage}</FormError>}
        {isSystemField && (
          <>
            <Spacer height={1} />
            <Paragraph>
              {`"${currentKeyPath ?? 'This'}"`} is a reserved key for system fields.
            </Paragraph>
          </>
        )}
        {isTopLevelSystemField &&
        !isEqual(matchingSystemField, fieldSchema) &&
        !fieldSchema.persisted ? (
          <>
            <Spacer height={2} />
            Once system fields are created, the configuration will be locked.
            <Spacer height={2} />
            <Button
              id='field-schema-panel__use-system-field__button'
              onClick={persistSystemField}
              variant='basic'
            >
              Create System Field
            </Button>
          </>
        ) : (
          <>
            <Spacer height={2} />
            <InputLabel htmlFor='field-schema-panel__field-type'>Type</InputLabel>
            <Controller
              name={`${fieldName}.type`}
              control={control}
              rules={{
                required: 'Field must have a type',
              }}
              render={({ field: { value } }) => (
                <Select
                  id='field-schema-panel__field-type'
                  value={value}
                  disabled={!canEditKeyType || isSystemField}
                  // There's not that many items, so just override the default
                  // max height to show them all without scroll
                  maxHeight={400}
                  onValueChange={(typeValue) => {
                    setValue(
                      fieldName,
                      getFieldSchemaWithUpdatedType(
                        fieldSchema,
                        typeValue as EditorableSchema['type'],
                      ),
                    );
                  }}
                >
                  {fieldTypeOptions}
                </Select>
              )}
            />
            {fieldSchema.type === FieldSchemaType.Array && (
              <>
                <Spacer height={2} />
                <InputLabel htmlFor='field-schema-panel__field-array-type'>
                  Array item type
                </InputLabel>
                <Select
                  id='field-schema-panel__field-array-type'
                  value={fieldSchema.config['item-schema']?.type}
                  disabled={isSystemField || !canEditKeyType}
                  onValueChange={(type: EditorableSchema['type']) => {
                    setValue(fieldName, {
                      ...fieldSchema,
                      config: {
                        ...fieldSchema.config,
                        'item-schema': getFieldSchemaWithUpdatedType(
                          DEFAULT_ARRAY_ITEM_SCHEMA,
                          type,
                        ),
                      },
                    });
                  }}
                >
                  {arrayTypeOptions}
                </Select>
              </>
            )}
            {fieldSchema.type === FieldSchemaType.Choices && (
              <>
                <Spacer height={2} />
                <InputLabel htmlFor='field-schema-panel__field-choices-input'>Options</InputLabel>
                <ReactCreatableSelect
                  size='md'
                  menuPlacement='bottom'
                  id='field-schema-panel__field-choices'
                  placeholder='To create a new choice, start typing'
                  value={fieldSchema.config.options ?? []}
                  options={(fieldSchema.config.options ?? []).map((value) => ({
                    value,
                    label: value,
                  }))}
                  disabled={!canEditField}
                  onChange={(value: string[]) => {
                    setValue(`${fieldName}.config.options`, value);
                    // Remove the default value if it is no longer in options
                    const currentDefaultValue = fieldSchema['default-value'];
                    if (!value.includes(currentDefaultValue)) {
                      setValue(`${fieldName}.default-value`, null);
                    }
                  }}
                  isMulti
                  portal
                />
              </>
            )}
            {fieldSchema.type === FieldSchemaType.MultiChoices && (
              <>
                <Spacer height={2} />
                <InputLabel htmlFor='field-schema-panel__field-multi-choices-input'>
                  Options
                </InputLabel>
                <ReactCreatableSelect
                  size='md'
                  menuPlacement='bottom'
                  id='field-schema-panel__field-multi-choices'
                  placeholder='To create a new choice, start typing'
                  value={fieldSchema.config.options ?? []}
                  options={(fieldSchema.config.options ?? []).map((value) => ({
                    value,
                    label: value,
                  }))}
                  disabled={!canEditField}
                  onChange={(value: string[]) => {
                    setValue(`${fieldName}.config.options`, value);
                    // Remove values from default-value array that are no longer
                    // in options
                    const currentDefaultValue = fieldSchema['default-value'];
                    setValue(
                      `${fieldName}.default-value`,
                      intersection(currentDefaultValue, value),
                    );
                  }}
                  isMulti
                  portal
                />
              </>
            )}
            {!isSystemField &&
              fieldSchema.type !== RelationSchemaType.RelationSchema &&
              DEFAULT_VALUE_SUPPORTED_FIELD_TYPES.includes(fieldSchema.type) && (
                <>
                  <Spacer height={2} />
                  <InputLabel htmlFor='field-schema-panel__field-default-value'>
                    Default value
                  </InputLabel>
                  <Controller
                    name={`${fieldName}.default-value`}
                    control={control}
                    render={({ field: { onChange } }) => (
                      <DefaultValueInput fieldSchema={fieldSchema} onChange={onChange} />
                    )}
                  />
                </>
              )}
            {renderAdditionalPanelContent?.(fieldSchema, fieldName, formContext)}
          </>
        )}
      </PaddedConfigSection>
      <FieldConfigurationControl>
        <FieldButtonGroup spacing={Spacing[2]}>
          <Button
            id='field-schema-panel__close'
            aria-label='Close field panel'
            variant='basic'
            onClick={onClose}
          >
            Close
          </Button>
        </FieldButtonGroup>
      </FieldConfigurationControl>
    </FieldSchemaPanelContainer>
  );
};

export default FormFieldSchemaPanel;
